import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface Verse {
  text: string;
  reference: string;
  why: string;
}

const verses: Verse[] = [
  {
    text: "Do not be anxious about anything, but in every situation, by prayer and petition, with thanksgiving, present your requests to God.",
    reference: "Philippians 4:6-7",
    why: "This verse reminds us to bring every worry to God in prayer and find peace that surpasses understanding."
  },
  {
    text: "So do not fear, for I am with you; do not be dismayed, for I am your God. I will strengthen you and help you; I will uphold you with my righteous right hand.",
    reference: "Isaiah 41:10",
    why: "This verse reassures us that God is always present to strengthen and support us in times of fear."
  },
  {
    text: "Cast all your anxiety on him because he cares for you.",
    reference: "1 Peter 5:7",
    why: "This verse reminds us that God cares deeply for us and invites us to give Him our burdens."
  }
];

export default function JourneyPeace() {
  const [currentVerseIndex, setCurrentVerseIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const navigate = useNavigate();

  const navigateVerse = (direction: number) => {
    setIsVisible(false);
    setTimeout(() => {
      setCurrentVerseIndex((prev) => (prev + direction + verses.length) % verses.length);
      setIsVisible(true);
    }, 500);
  };

  const currentVerse = verses[currentVerseIndex];

  return (
    <div className="min-h-screen bg-[#f9f9f9] py-8 px-4 overflow-y-auto pb-24">
      <div className="max-w-3xl mx-auto pb-8">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img 
            src="/lovable-uploads/97cec83c-b4fa-48c3-a12a-a678346efa1f.png" 
            alt="Grace Awakens Logo" 
            className="h-16 w-auto"
          />
        </div>

        {/* Scripture App Container */}
        <div className="bg-white rounded-lg shadow-lg p-8 relative mb-8">
          <h1 className="text-2xl font-semibold text-gray-800 mb-6">
            Journey to Peace
          </h1>

          {/* Navigation Arrows */}
          <button
            onClick={() => navigateVerse(-1)}
            className="absolute left-[-20px] top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/80 rounded-full shadow-md flex items-center justify-center text-gray-600 hover:text-gray-800 transition-colors"
            aria-label="Previous verse"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>

          {/* Scripture Content */}
          <div className={`transition-opacity duration-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            <p className="text-lg text-gray-700 mb-4">
              "{currentVerse.text}"
            </p>
            <p className="text-grace-DEFAULT font-semibold mb-3">
              {currentVerse.reference}
            </p>
            <p className="text-gray-600 italic mb-6">
              {currentVerse.why}
            </p>
          </div>

          <button
            onClick={() => navigateVerse(1)}
            className="absolute right-[-20px] top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/80 rounded-full shadow-md flex items-center justify-center text-gray-600 hover:text-gray-800 transition-colors"
            aria-label="Next verse"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </div>

        {/* Back Button with Arrow */}
        <div className="text-center">
          <Button
            onClick={() => navigate(-1)}
            variant="secondary"
            className="hover:bg-gray-100 gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Journeys
          </Button>
        </div>
      </div>
    </div>
  );
}